import { Card, CardHeader, Divider, Box, Typography, useTheme } from "@mui/material";
import { useListContext } from "ra-core";
import { FC } from "react";
import Chart from 'react-apexcharts'
import { FunctionField } from "../field";
import { Datagrid } from "../list/datagrid";
import Scrollbar from "../Scrollbar";
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import DateField2 from "../field/DateField2";
import { RecPatch } from "~/dataProvider/tools";
import { genUrlWithHashid } from "~/utils/hashid";
import { Link as RouterLink } from 'react-router-dom'
import { DashboardDatabaseStatisResponseDto } from "~/gen/api/governance";
import { merge } from "lodash";
import { AtopFuncArea, SearchField } from "../list/datagridFunctionWidgets";
import DatabaseNameField from "../databases/DatabaseNameField";
import { EncryptedDatabasesContextProvider } from "../databases/useEncryptedDatabases";
import { nonSqlDbTypes } from "../obfuscation/defs";


const DatabaseUpdatesTable: FC = () => {
  const listContext = useListContext()
  const theme = useTheme()
  
  return (
    <EncryptedDatabasesContextProvider>
      <Card>
        <CardHeader title="Database Updates" />
        <Divider />
        <AtopFuncArea remoteDataSource={true}>
          <SearchField
            sourceKey="term"
            sx={{ width: 280 }}
            TextFieldProps={{ placeholder: 'Search by Database Name' }} 
          />
        </AtopFuncArea>
        <Scrollbar>
          <Box sx={{ minWidth: 1150 }}>

            <Datagrid 
              hasBulkActions={false} 
              {...listContext}
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
                '& tr div.MuiBox-root': {
                  '& div.encryptedDbTip': { top: theme => theme.spacing(-5) },
                  '& span.encryptedDbTipText': { top: theme => theme.spacing(-5) },
                },
              }}
            >
              <DatabaseNameField label="Name" />
              <DateField2 source="lastBackupTime" label="Last Successful Backup" showTime />
              <FunctionField 
                label="Field Classifications"
                sortable={false}
                render={(d?:DashboardDatabaseStatisResponseDto&RecPatch) => {
                  if (!d || !d.totalTableCount) { 
                    return (<Typography variant="body1" sx={{ ml: 4, my: 2 }}>N/A</Typography>)
                  }

                  const chart = merge({}, chartBaseOptions, {
                    options: {
                      colors: colors[3],
                      theme: { mode: theme.palette.mode }
                    },
                    series: [d.publicCount, d.sensitiveCount, d.confidentialCount, d.regulatedCount, d.naCount]
                  });
                  return (
                    <Box sx={{ mt: '-5px', mb: '-15px', '& div': { display: 'inline' } }}>
                      <RouterLink aria-label="go to edit fields classifications"
                        to={genUrlWithHashid(
                          nonSqlDbTypes.has(d.databaseType!) 
                            ? `/encryption/mappings/$0/ns/show`
                            : `/encryption/mappings/$0/show`, 
                          d.id, d.name
                        )} 
                      >
                        <Chart 
                          height="100"
                          width="100"
                          type="donut"
                          {...(chart as any)}
                        />
                      </RouterLink>
                    </Box>
                  )
                }}
              />
              <FunctionField 
                label="Table Ownership" 
                sortable={false} 
                render={(d?:DashboardDatabaseStatisResponseDto&RecPatch) => {
                  if (!d || !d.totalTableCount) { 
                    return (<Typography variant="body1" >N/A</Typography>)
                  }

                  return (
                    <Box sx={{ '& a': { textDecoration: 'none' } }}>
                      <RouterLink to={genUrlWithHashid(`/governance/data-ownership/$0/show`, d.id, d.name)} aria-label="go to edit tables ownership" >
                        <Typography 
                          component="span" 
                          variant="h6" 
                          color={(d?.ownedTableCount || 0) === 0 ? 'error.main' : ((d?.ownedTableCount || 0) < 5) ? 'warning.main' : 'success.main' }
                        >
                          {d?.ownedTableCount || 0}
                          <Typography component="span" variant="body2" sx={{ ml: 1, color: "text.primary" }}>
                            / {d?.totalTableCount || 0}
                          </Typography>
                        </Typography>
                      </RouterLink>
                    </Box>
                  )
                }}
              />
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore
          <DefaultPagination {...listContext} />}
      </Card>
    </EncryptedDatabasesContextProvider>
  )
}

export default DatabaseUpdatesTable;

const chartBaseOptions = {
  options: {
    chart: {
      background: 'transparent',
      stacked: false,
      toolbar: { show: false },
      zoom: false
    },
    dataLabels: { enabled: false },
    labels: ['Public', 'Sensitive', 'Confidential', 'Regulated', 'Pending Review'],
    legend: { show: false },
    stroke: { width: 0 },
  },
}

const colors = [
  ['#45CA35', '#CA6F35', '#BA35CA', '#3590CA', '#ff4c5b'],
  ['#90DD75', '#DBDBDB', '#432D14', '#0013C3', '#E50E00'],
  ['#45CA35', '#DBDBDB', '#BA35CA', '#3590CA', '#ff4c5b'],
  ['#45CA35', '#b6e04d', '#BA35CA', '#3590CA', '#cd7f18'],
  ['#45CA35', '#dafa8a', '#BA35CA', '#3590CA', '#cd7f18'],
]

